import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Save,
  Search,
  Building2,
  Share2,
  Bot,
  Shield,
  Loader2,
  Plus,
  X,
  Globe,
  ExternalLink,
  BarChart,
  Upload,
  Image as ImageIcon,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { AdminLayout } from '../../components/Admin/AdminLayout';
import { ToggleSwitch } from '../../components/Common/ToggleSwitch';
import { useAuthStore } from '../../store/authStore';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:5000';
const PUBLIC_ORIGIN = import.meta.env.VITE_PUBLIC_ORIGIN || 'https://hitzlink.com';

interface SeoSettings {
  site_name?: string | null;
  site_url?: string | null;
  seo_title?: string | null;
  seo_description?: string | null;
  seo_keywords?: string | null;
  seo_og_image?: string | null;
  favicon_url?: string | null;

  organization_name?: string | null;
  organization_logo_url?: string | null;
  organization_same_as?: string[] | null;
  twitter_handle?: string | null;
  default_author_name?: string | null;
  default_author_bio?: string | null;

  blog_enabled?: boolean;
  ai_indexing_enabled?: boolean;
  noindex_paths?: string[] | null;

  google_analytics_id?: string | null;
  google_tag_manager_id?: string | null;
  facebook_pixel_id?: string | null;
  tiktok_pixel_id?: string | null;
}

const DEFAULTS: SeoSettings = {
  site_name: 'HitzLink',
  site_url: 'https://hitzlink.com',
  organization_same_as: [],
  blog_enabled: true,
  ai_indexing_enabled: true,
  noindex_paths: ['/api/', '/admin/', '/dashboard/', '/builder/', '/p/'],
};

export const AdminSeoSettings: React.FC = () => {
  const token = useAuthStore((s) => s.token);
  const [settings, setSettings] = useState<SeoSettings>(DEFAULTS);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [isUploadingFavicon, setIsUploadingFavicon] = useState(false);
  const faviconInputRef = useRef<HTMLInputElement>(null);

  const authHeader = useMemo<Record<string, string>>(
    () => (token ? { Authorization: `Bearer ${token}` } : ({} as Record<string, string>)),
    [token]
  );

  useEffect(() => {
    if (!token) return;
    (async () => {
      try {
        const res = await fetch(`${API_URL}/admin/settings`, { headers: authHeader });
        if (!res.ok) throw new Error();
        const data = await res.json();
        // Server may return JSONB / array fields as null on rows that
        // existed before migration 023. Coerce so the UI doesn't blow
        // up on `.map` calls.
        setSettings({
          ...DEFAULTS,
          ...data,
          organization_same_as: Array.isArray(data.organization_same_as) ? data.organization_same_as : [],
          noindex_paths: Array.isArray(data.noindex_paths) && data.noindex_paths.length > 0
            ? data.noindex_paths
            : DEFAULTS.noindex_paths,
        });
      } catch {
        toast.error('Failed to load settings');
      } finally {
        setLoading(false);
      }
    })();
  }, [token, authHeader]);

  const patch = <K extends keyof SeoSettings>(key: K, value: SeoSettings[K]) => {
    setSettings((prev) => ({ ...prev, [key]: value }));
  };

  const handleFaviconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml', 'image/x-icon', 'image/vnd.microsoft.icon'];
    if (!allowedTypes.includes(file.type)) { toast.error('Invalid file type'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('File must be < 5 MB'); return; }
    setIsUploadingFavicon(true);
    try {
      const formData = new FormData();
      formData.append('favicon', file);
      const res = await fetch(`${API_URL}/admin/upload-favicon`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: formData,
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error || 'Upload failed'); }
      const data = await res.json();
      patch('favicon_url', data.url);
      toast.success('Favicon uploaded');
    } catch (err: any) {
      toast.error(err.message || 'Favicon upload failed');
    } finally {
      setIsUploadingFavicon(false);
      if (faviconInputRef.current) faviconInputRef.current.value = '';
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch(`${API_URL}/admin/settings`, {
        method: 'PUT',
        headers: { ...authHeader, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          site_name: settings.site_name,
          site_url: settings.site_url,
          seo_title: settings.seo_title,
          seo_description: settings.seo_description,
          seo_keywords: settings.seo_keywords,
          seo_og_image: settings.seo_og_image,
          favicon_url: settings.favicon_url,
          organization_name: settings.organization_name,
          organization_logo_url: settings.organization_logo_url,
          organization_same_as: settings.organization_same_as || [],
          twitter_handle: settings.twitter_handle,
          default_author_name: settings.default_author_name,
          default_author_bio: settings.default_author_bio,
          blog_enabled: settings.blog_enabled,
          ai_indexing_enabled: settings.ai_indexing_enabled,
          noindex_paths: settings.noindex_paths || [],
          google_analytics_id: settings.google_analytics_id,
          google_tag_manager_id: settings.google_tag_manager_id,
          facebook_pixel_id: settings.facebook_pixel_id,
          tiktok_pixel_id: settings.tiktok_pixel_id,
        }),
      });
      if (!res.ok) throw new Error();
      toast.success('SEO settings saved');
    } catch {
      toast.error('Save failed');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center min-h-[60vh]">
          <Loader2 className="w-6 h-6 animate-spin text-blue-600" />
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      <div className="p-4 sm:p-6 lg:p-8 max-w-4xl mx-auto space-y-6">
        <header className="flex flex-wrap items-center justify-between gap-3">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
              <Search className="w-7 h-7 text-blue-600" />
              SEO &amp; AEO Settings
            </h1>
            <p className="text-sm text-gray-500 mt-1">
              These fields feed JSON-LD, Open Graph, robots.txt, and AI-crawler endpoints across the entire platform.
            </p>
          </div>
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium shadow-sm disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save changes
          </button>
        </header>

        {/* Quick links to verify settings live */}
        <div className="flex flex-wrap gap-2 p-3 bg-blue-50 border border-blue-200 rounded-lg text-sm">
          <span className="text-blue-900 font-medium mr-2">Preview live:</span>
          {[
            ['robots.txt', '/robots.txt'],
            ['sitemap.xml', '/sitemap.xml'],
            ['llms.txt', '/llms.txt'],
            ['Blog', '/blog'],
            ['FAQ', '/faq'],
          ].map(([label, path]) => (
            <a
              key={path}
              href={`${PUBLIC_ORIGIN}${path}`}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 text-blue-700 hover:text-blue-900 hover:underline"
            >
              <ExternalLink className="w-3 h-3" />
              {label}
            </a>
          ))}
        </div>

        {/* Organization */}
        <Section icon={<Building2 className="w-5 h-5 text-blue-600" />} title="Organization (E-E-A-T signals)" description="Drives JSON-LD Organization across every SSR'd page.">
          <FieldGroup>
            <Field label="Organization name">
              <input
                type="text"
                value={settings.organization_name || ''}
                onChange={(e) => patch('organization_name', e.target.value)}
                placeholder={settings.site_name || 'HitzLink'}
                className="input"
              />
            </Field>
            <Field label="Organization logo URL">
              <input
                type="url"
                value={settings.organization_logo_url || ''}
                onChange={(e) => patch('organization_logo_url', e.target.value)}
                placeholder="https://hitzlink.com/logo.png"
                className="input"
              />
            </Field>
          </FieldGroup>
          <Field label="Default author name (fallback for Articles)">
            <input
              type="text"
              value={settings.default_author_name || ''}
              onChange={(e) => patch('default_author_name', e.target.value)}
              placeholder="HitzLink Team"
              className="input"
            />
          </Field>
          <Field label="Default author bio">
            <textarea
              value={settings.default_author_bio || ''}
              onChange={(e) => patch('default_author_bio', e.target.value)}
              rows={2}
              className="input resize-none"
            />
          </Field>
        </Section>

        {/* Social */}
        <Section icon={<Share2 className="w-5 h-5 text-blue-600" />} title="Social profiles (sameAs)" description="Each URL becomes an entry in Organization.sameAs and helps Google connect your brand.">
          <Field label="Twitter / X handle (without @)">
            <input
              type="text"
              value={settings.twitter_handle || ''}
              onChange={(e) => patch('twitter_handle', e.target.value.replace(/^@/, ''))}
              placeholder="hitzlink"
              className="input"
            />
          </Field>
          <ArrayEditor
            label="Social profile URLs (sameAs)"
            placeholder="https://twitter.com/hitzlink"
            values={settings.organization_same_as || []}
            onChange={(next) => patch('organization_same_as', next)}
          />
        </Section>

        {/* Favicon */}
        <Section icon={<ImageIcon className="w-5 h-5 text-indigo-600" />} title="Website Favicon" description="The small icon shown in browser tabs and bookmarks.">
          <div className="flex flex-col sm:flex-row gap-4 items-start">
            <div className="flex-shrink-0">
              {settings.favicon_url ? (
                <div className="w-16 h-16 rounded-lg border border-gray-200 bg-white flex items-center justify-center overflow-hidden">
                  <img src={settings.favicon_url} alt="Favicon" className="w-12 h-12 object-contain" onError={(e) => { (e.target as HTMLImageElement).style.display = 'none'; }} />
                </div>
              ) : (
                <div className="w-16 h-16 rounded-lg border-2 border-dashed border-gray-300 flex items-center justify-center">
                  <ImageIcon className="w-8 h-8 text-gray-300" />
                </div>
              )}
            </div>
            <div className="flex-1 space-y-2">
              <Field label="Favicon URL">
                <input type="text" value={settings.favicon_url || ''} onChange={(e) => patch('favicon_url', e.target.value)} placeholder="https://example.com/favicon.png" className="input" />
              </Field>
              <div className="flex items-center gap-2">
                <input ref={faviconInputRef} type="file" accept="image/png,image/jpeg,image/svg+xml,image/x-icon,image/vnd.microsoft.icon" onChange={handleFaviconUpload} className="hidden" />
                <button type="button" onClick={() => faviconInputRef.current?.click()} disabled={isUploadingFavicon} className="inline-flex items-center gap-1.5 px-3 py-1.5 text-sm bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg disabled:opacity-50">
                  {isUploadingFavicon ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Upload className="w-3.5 h-3.5" />}
                  Upload Favicon
                </button>
                <span className="text-xs text-gray-500">PNG, ICO, SVG (Max 5MB)</span>
              </div>
            </div>
          </div>
        </Section>

        {/* Default SEO */}
        <Section icon={<Globe className="w-5 h-5 text-blue-600" />} title="Default SEO & Meta Tags" description="Used when a page or article doesn't specify its own.">
          <FieldGroup>
            <Field label="SEO Title">
              <input
                type="text"
                value={settings.seo_title || ''}
                onChange={(e) => patch('seo_title', e.target.value)}
                maxLength={120}
                placeholder="HitzLink - Professional Landing Page Builder"
                className="input"
              />
              <p className="text-xs text-gray-400 mt-1">{(settings.seo_title || '').length}/60 recommended</p>
            </Field>
            <Field label="OG image URL (1200x630 ideal)">
              <input
                type="url"
                value={settings.seo_og_image || ''}
                onChange={(e) => patch('seo_og_image', e.target.value)}
                placeholder="https://hitzlink.com/og-image.png"
                className="input"
              />
            </Field>
          </FieldGroup>
          <Field label="SEO Description">
            <textarea
              value={settings.seo_description || ''}
              onChange={(e) => patch('seo_description', e.target.value)}
              rows={2}
              maxLength={300}
              placeholder="Build stunning landing pages in minutes with our drag & drop builder..."
              className="input resize-none"
            />
            <p className="text-xs text-gray-400 mt-1">{(settings.seo_description || '').length}/160 recommended</p>
          </Field>
          <Field label="Keywords (comma-separated)">
            <input
              type="text"
              value={settings.seo_keywords || ''}
              onChange={(e) => patch('seo_keywords', e.target.value)}
              placeholder="landing page, builder, drag and drop, marketing"
              className="input"
            />
          </Field>
        </Section>

        {/* Analytics & Tracking */}
        <Section icon={<BarChart className="w-5 h-5 text-green-600" />} title="Analytics & Tracking Pixels" description="Tracking codes for the main domain (Home, Platform pages). Users set their own per-landing-page in the Builder.">
          <FieldGroup>
            <Field label="Google Analytics ID">
              <input type="text" value={settings.google_analytics_id || ''} onChange={(e) => patch('google_analytics_id', e.target.value)} placeholder="G-XXXXXXXXXX" className="input font-mono" />
            </Field>
            <Field label="Google Tag Manager ID">
              <input type="text" value={settings.google_tag_manager_id || ''} onChange={(e) => patch('google_tag_manager_id', e.target.value)} placeholder="GTM-XXXXXXX" className="input font-mono" />
            </Field>
          </FieldGroup>
          <FieldGroup>
            <Field label="Facebook Pixel ID">
              <input type="text" value={settings.facebook_pixel_id || ''} onChange={(e) => patch('facebook_pixel_id', e.target.value)} placeholder="123456789012345" className="input font-mono" />
            </Field>
            <Field label="TikTok Pixel ID">
              <input type="text" value={settings.tiktok_pixel_id || ''} onChange={(e) => patch('tiktok_pixel_id', e.target.value)} placeholder="XXXXXXXXXXXXXX" className="input font-mono" />
            </Field>
          </FieldGroup>
        </Section>

        {/* AI Indexing */}
        <Section icon={<Bot className="w-5 h-5 text-blue-600" />} title="AI crawlers (AEO)" description="Controls llms.txt, llms-full.txt, and the named-bot allow rules in robots.txt.">
          <div className="flex items-center justify-between gap-4">
            <div>
              <p className="font-medium text-gray-900">Allow AI crawlers</p>
              <p className="text-sm text-gray-500">GPTBot, ClaudeBot, PerplexityBot, Google-Extended, etc.</p>
            </div>
            <ToggleSwitch
              enabled={!!settings.ai_indexing_enabled}
              onChange={(v) => patch('ai_indexing_enabled', v)}
            />
          </div>
          <div className="flex items-center justify-between gap-4 pt-3 border-t border-gray-100">
            <div>
              <p className="font-medium text-gray-900">Enable /blog and /faq</p>
              <p className="text-sm text-gray-500">Switch off to return 404 for the blog index without taking content down.</p>
            </div>
            <ToggleSwitch
              enabled={!!settings.blog_enabled}
              onChange={(v) => patch('blog_enabled', v)}
            />
          </div>
        </Section>

        {/* Robots noindex paths */}
        <Section icon={<Shield className="w-5 h-5 text-blue-600" />} title="Robots disallow paths" description="Path prefixes appended as Disallow rules in robots.txt for every crawler.">
          <ArrayEditor
            label="Disallow rules"
            placeholder="/admin/"
            values={settings.noindex_paths || []}
            onChange={(next) => patch('noindex_paths', next)}
          />
        </Section>

        <div className="pt-4">
          <button
            onClick={handleSave}
            disabled={saving}
            className="inline-flex items-center gap-2 px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white rounded-lg font-medium shadow-sm disabled:opacity-50"
          >
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            Save changes
          </button>
        </div>
      </div>

      {/* Inline utility classes — the codebase doesn't ship a shared
          `<Input>` so we co-locate the styles here. Keeping them on
          the AdminLayout-scoped DOM means they don't leak to the
          user dashboard. */}
      <style>{`
        .input { width: 100%; padding: 8px 12px; border: 1px solid #d1d5db; border-radius: 8px; font-size: 14px; }
        .input:focus { outline: none; border-color: transparent; box-shadow: 0 0 0 2px #3b82f6; }
      `}</style>
    </AdminLayout>
  );
};

// ── Helpers ───────────────────────────────────────────────────────

function Section({ icon, title, description, children }: {
  icon: React.ReactNode;
  title: string;
  description: string;
  children: React.ReactNode;
}) {
  return (
    <section className="bg-white rounded-xl border border-gray-200 p-5">
      <header className="flex items-start gap-3 mb-4">
        <div className="mt-0.5">{icon}</div>
        <div>
          <h2 className="text-base font-semibold text-gray-900">{title}</h2>
          <p className="text-sm text-gray-500">{description}</p>
        </div>
      </header>
      <div className="space-y-3">{children}</div>
    </section>
  );
}

function FieldGroup({ children }: { children: React.ReactNode }) {
  return <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>;
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
      {children}
    </label>
  );
}

function ArrayEditor({ label, placeholder, values, onChange }: {
  label: string;
  placeholder?: string;
  values: string[];
  onChange: (next: string[]) => void;
}) {
  const [draft, setDraft] = useState('');
  return (
    <div>
      <span className="block text-xs font-medium text-gray-700 mb-1">{label}</span>
      <div className="space-y-2">
        {values.map((v, i) => (
          <div key={i} className="flex gap-2">
            <input
              type="text"
              value={v}
              onChange={(e) => {
                const next = values.slice();
                next[i] = e.target.value;
                onChange(next);
              }}
              className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
            <button
              type="button"
              onClick={() => onChange(values.filter((_, j) => j !== i))}
              className="px-2 py-1 text-red-600 hover:bg-red-50 rounded-md"
              aria-label="Remove"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        ))}
        <div className="flex gap-2">
          <input
            type="text"
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && draft.trim()) {
                e.preventDefault();
                onChange([...values, draft.trim()]);
                setDraft('');
              }
            }}
            placeholder={placeholder}
            className="flex-1 px-3 py-2 text-sm border border-gray-300 border-dashed rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
          />
          <button
            type="button"
            onClick={() => {
              if (!draft.trim()) return;
              onChange([...values, draft.trim()]);
              setDraft('');
            }}
            className="inline-flex items-center gap-1 px-3 py-1 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
          >
            <Plus className="w-3.5 h-3.5" />
            Add
          </button>
        </div>
      </div>
    </div>
  );
}

export default AdminSeoSettings;
